/**
 * Custom Curved SVG Page Transition (Inspired by manayerbamate.com)
 * AJAX-based SPA page loading for seamless, continuous animations.
 */
(function () {
  // Disable transition in theme editor/design mode to prevent visual glitches in customizer
  if (window.Shopify && window.Shopify.designMode) {
    return;
  }

  // Configuration
  const ANIMATION_DURATION = 600; // ms
  const EXCLUDED_CLASSES = ['no-transition', 'cart-drawer-trigger', 'quick-add-button'];

  // Easing function (easeInOutCubic for organic fluid look)
  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // Animation controller
  function animatePath({ duration, onUpdate, onComplete }) {
    const startTime = performance.now();

    function step(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      onUpdate(progress);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else if (onComplete) {
        onComplete();
      }
    }

    requestAnimationFrame(step);
  }

  // Initialize page transition overlay
  let overlay, path;

  function initOverlay() {
    if (document.getElementById('page-transition-overlay')) {
      overlay = document.getElementById('page-transition-overlay');
      path = document.getElementById('page-transition-path');
      return;
    }

    overlay = document.createElement('div');
    overlay.id = 'page-transition-overlay';
    overlay.innerHTML = `
      <svg viewBox="0 0 100 100" preserveAspectRatio="none">
        <path id="page-transition-path" d="M 0 100 L 100 100 L 100 100 Q 50 100 0 100 Z"></path>
      </svg>
    `;

    document.body.appendChild(overlay);
    path = document.getElementById('page-transition-path');
  }

  // Intro transition (revealing page content)
  function playIntro() {
    initOverlay();
    overlay.classList.add('is-active');

    animatePath({
      duration: ANIMATION_DURATION,
      onUpdate: (progress) => {
        const Y_peak = 100 - easeInOutCubic(progress) * 100;
        const Y_sides = 100 - easeInOutCubic(Math.max(0, progress - 0.25) / 0.75) * 100;
        
        const d = `M 0 ${Y_sides} Q 50 ${Y_peak} 100 ${Y_sides} L 100 0 L 0 0 Z`;
        path.setAttribute('d', d);
      },
      onComplete: () => {
        overlay.classList.remove('is-active');
        path.setAttribute('d', 'M 0 100 L 100 100 L 100 100 Q 50 100 0 100 Z');
      }
    });
  }

  // Outro transition (covering page content)
  function playOutro(onComplete) {
    initOverlay();
    overlay.classList.add('is-active');

    const safetyTimeout = setTimeout(() => {
      overlay.classList.remove('is-active');
      if (onComplete) onComplete();
    }, 3000);

    animatePath({
      duration: ANIMATION_DURATION,
      onUpdate: (progress) => {
        const Y_peak = 100 - easeInOutCubic(progress) * 100;
        const Y_sides = 100 - easeInOutCubic(Math.max(0, progress - 0.25) / 0.75) * 100;
        
        const d = `M 0 100 L 100 100 L 100 ${Y_sides} Q 50 ${Y_peak} 0 ${Y_sides} Z`;
        path.setAttribute('d', d);
      },
      onComplete: () => {
        clearTimeout(safetyTimeout);
        if (onComplete) onComplete();
      }
    });
  }

  // Re-execute scripts dynamically in the swapped container
  function executeScripts(container) {
    const scripts = Array.from(container.querySelectorAll('script'));
    scripts.forEach((oldScript) => {
      // Avoid re-executing global page-transition control script or view transitions setup script
      if (oldScript.src && (oldScript.src.includes('page-transition') || oldScript.src.includes('view-transitions'))) {
        return;
      }
      
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      
      try {
        oldScript.parentNode.replaceChild(newScript, oldScript);
      } catch (err) {
        console.warn('Failed to execute script:', err);
      }
    });
  }

  // Helper to check if a container contains any Shopify app blocks
  function hasAppBlock(container) {
    if (!container) return false;
    return container.querySelector('.shopify-app-block, [class*="shopify-app-block"], [id*="app-block"]') !== null;
  }

  // Swap HTML contents seamlessly
  function swapContent(doc, targetUrl, shouldPushState = true) {
    // 1. Update Title
    document.title = doc.title;

    // 2. Swap Body Class (does not destroy DOM nodes)
    document.body.className = doc.body.className;

    // 3. Swap Main Content
    const currentMain = document.getElementById('MainContent');
    const newMain = doc.getElementById('MainContent');
    if (currentMain && newMain) {
      currentMain.innerHTML = newMain.innerHTML;
      // Copy attributes of MainContent (like data-template, data-product-transition, etc.)
      Array.from(newMain.attributes).forEach((attr) => {
        currentMain.setAttribute(attr.name, attr.value);
      });
    }

    // 4. Scroll to top
    window.scrollTo(0, 0);

    // 5. Push History State if applicable
    if (shouldPushState) {
      window.history.pushState(null, '', targetUrl);
    }

    // 6. Execute scripts inside MainContent
    if (currentMain) {
      executeScripts(currentMain);
    }

    // 7. Re-trigger custom document load events so that other theme assets listen to it
    document.dispatchEvent(new Event('DOMContentLoaded'));
    window.dispatchEvent(new Event('load'));
  }

  // Determine if a link click should be intercepted
  function shouldIntercept(e) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;

    const link = e.target.closest('a');
    if (!link) return false;

    if (link.target === '_blank') return false;
    if (link.hasAttribute('download')) return false;
    if (link.getAttribute('rel') === 'external') return false;

    const hasExcludedClass = EXCLUDED_CLASSES.some(className => link.classList.contains(className));
    if (hasExcludedClass) return false;

    const href = link.getAttribute('href');
    if (!href) return false;

    if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) {
      return false;
    }

    try {
      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin) return false;
      
      // Ignore same page anchor navigation
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash !== '') {
        return false;
      }
      
      // Ignore Shopify customer logout link or checkout direct paths
      if (url.pathname.includes('/account/logout') || url.pathname.includes('/checkout')) {
        return false;
      }
    } catch (_) {
      return false;
    }

    return true;
  }

  // Intercept links click and navigate using AJAX
  document.addEventListener('click', function (e) {
    if (shouldIntercept(e)) {
      e.preventDefault();
      const link = e.target.closest('a');
      const targetUrl = link.href;

      // Start fetching and transition parallelly
      const outroPromise = new Promise((resolve) => {
        playOutro(resolve);
      });

      const fetchPromise = fetch(targetUrl)
        .then((r) => {
          if (!r.ok) throw new Error('Network response not ok');
          return r.text();
        });

      Promise.all([outroPromise, fetchPromise])
        .then(([_, html]) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const targetMain = doc.getElementById('MainContent');
          const currentMain = document.getElementById('MainContent');

          if (hasAppBlock(targetMain) || hasAppBlock(currentMain)) {
            window.location.href = targetUrl;
            return;
          }

          swapContent(doc, targetUrl, true);
          playIntro();
        })
        .catch((err) => {
          console.error("Page transition fetch failed, navigating normally", err);
          window.location.href = targetUrl;
        });
    }
  });

  // Handle browser back/forward buttons (history popstate)
  window.addEventListener('popstate', function () {
    const targetUrl = window.location.href;

    const outroPromise = new Promise((resolve) => {
      playOutro(resolve);
    });

    const fetchPromise = fetch(targetUrl)
      .then((r) => {
        if (!r.ok) throw new Error('Network response not ok');
        return r.text();
      });

    Promise.all([outroPromise, fetchPromise])
      .then(([_, html]) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const targetMain = doc.getElementById('MainContent');
        const currentMain = document.getElementById('MainContent');

        if (hasAppBlock(targetMain) || hasAppBlock(currentMain)) {
          window.location.reload();
          return;
        }

        swapContent(doc, targetUrl, false); // false = do not pushState again
        playIntro();
      })
      .catch((err) => {
        console.error("Page transition popstate fetch failed, reloading page", err);
        window.location.reload();
      });
  });

  // Run intro on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', playIntro);
  } else {
    playIntro();
  }
})();
